import { formatAmount, ROLE_OWNER, transactionTypeValues } from "@/lib/utils";
import { RootState } from "@/redux/store";
import {
  useGetARPropertyByIdReport,
  useGetARPropertyReport,
  useGetPropertiesByCommunityAndUser,
} from "@/services";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const usePropertyReport = ({ reportDate }: { reportDate: any }) => {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const [tableData, setTableData] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<any>("");

  const [summaryCard, setSummaryCard] = useState([
    {
      title: "Total Records",
      count: 0,
      key: "totalRecord",
    },
    {
      title: "Outstanding A/R",
      count: 0,
      key: "totalOutstanding",
    },
    {
      title: "Uncleared A/R",
      count: 0,
      key: "unclearedAr",
    },
    {
      title: "Uncleared Collections",
      count: 0,
      key: "openCollection",
    },
  ]);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentRole = useSelector(
    (state: RootState) => state.currentData.currentRole
  );

  const isOwner = currentRole == ROLE_OWNER;

  const { data: propertyList, isLoading: isLoadingProperties } =
    useGetPropertiesByCommunityAndUser({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!(communityDetail?.communityId && isOwner),
      },
    });

  const {
    data: detailedReport,
    isLoading: isLoadingDetailedReport,
    refetch: refetchDetailedReport,
  } = useGetARPropertyReport({
    communityId: communityDetail?.communityId,
    ...(reportDate
      ? { reportDate: moment(reportDate).format("YYYY-MM-DD") }
      : {}),
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: detailedPropertyReport } = useGetARPropertyByIdReport({
    communityId: communityDetail?.communityId,
    propertyId: selectedPropertyId,
    queryParams: {
      enabled: !!selectedPropertyId,
    },
  });

  useEffect(() => {
    if (detailedPropertyReport) {
      const _tableData = [...tableData];
      const idx = _tableData?.findIndex(
        (val: any) => val.propertyId == selectedPropertyId
      );
      _tableData[idx] = {
        ..._tableData[idx],
        children: detailedPropertyReport?.data?.data?.sort(
          (a: any, b: any) =>
            new Date(b.billedDate).getTime() - new Date(a.billedDate).getTime()
        ),
      };
      setTableData([..._tableData]);
    }
  }, [detailedPropertyReport]);

  useEffect(() => {
    refetchDetailedReport();
  }, [reportDate]);

  useEffect(() => {
    if (detailedReport?.data) {
      let unclearedAr = 0,
        openCollection = 0,
        outstanding = 0;
      const detailedData = detailedReport?.data;

      detailedData?.data?.map((data: any) => {
        unclearedAr += data.pendingBillingAmount;
        openCollection += data.collectionsAmount;
        outstanding +=
          data.pendingBillingAmount +
          data.otherChargesAmount -
          data.collectionsAmount;
      });

      const cards = summaryCard?.map((data: any) => {
        if (data.key == "unclearedAr") {
          return {
            ...data,
            count: unclearedAr,
          };
        }

        if (data.key == "totalOutstanding") {
          return {
            ...data,
            count: outstanding,
          };
        }

        if (data.key == "openCollection") {
          return {
            ...data,
            count: openCollection,
          };
        }

        return {
          ...data,
          count: data?.key?.toLowerCase()?.includes("amount")
            ? formatAmount(detailedData?.[data?.key] || "0")
            : detailedData?.[data?.key] || "0",
        };
      });

      setSummaryCard([...cards]);
    }

    if (isOwner && propertyList?.data) {
      const propertyIds = propertyList?.data?.map((val: any) => val.propertyId);

      setTableData(
        detailedReport?.data?.data?.filter((val: any) =>
          propertyIds.includes(val.propertyId)
        ) || []
      );
    } else {
      setTableData(detailedReport?.data?.data || []);
    }
  }, [detailedReport, propertyList]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "propertyName",
      header: <div>Property Name</div>,
    },
    // {
    //   accessorKey: "totalBillingAmount",
    //   header: <div>Total Billing</div>,
    //   cell: (row: any) => {
    //     return row?.row.original?.category?.toLowerCase()?.includes("count")
    //       ? row?.row.original?.totalBillingAmount
    //       : formatAmount(row?.row.original?.totalBillingAmount || "0");
    //   },
    // },
    {
      accessorKey: "outstandingAr",
      meta: { align: "right" },
      header: <div>Outstanding A/R</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.pendingBillingAmount +
              row?.row.original?.otherChargesAmount -
              row?.row.original?.collectionsAmount
          : formatAmount(
              row?.row.original?.pendingBillingAmount +
                row?.row.original?.otherChargesAmount -
                row?.row.original?.collectionsAmount || "0"
            );
      },
    },
    {
      accessorKey: "pendingBillingAmount",
      meta: { align: "right" },
      header: <div>Uncleared A/R</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.pendingBillingAmount
          : formatAmount(row?.row.original?.pendingBillingAmount || "0");
      },
    },
    {
      accessorKey: "otherChargesAmount",
      meta: { align: "right" },
      header: <div>Uncleared Other Chg.</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.otherChargesAmount
          : formatAmount(row?.row.original?.otherChargesAmount || "0");
      },
    },
    {
      accessorKey: "collectionsAmount",
      meta: { align: "right" },
      header: <div>Uncleared Collections</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.collectionsAmount
          : formatAmount(
              Number(row?.row.original?.collectionsAmount) === 0
                ? "0"
                : `-${row?.row.original?.collectionsAmount}`
            );
      },
    },
    {
      accessorKey: "agingBucketNotDue",
      meta: { align: "right" },
      header: <div>Not Due</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucketNotDue
          : formatAmount(row?.row.original?.agingBucketNotDue || "0");
      },
    },
    {
      accessorKey: "agingBucket0To30",
      meta: { align: "right" },
      header: (
        <div>
          Due In
          <br />
          0-30 Days
        </div>
      ),
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucket0To30
          : formatAmount(row?.row.original?.agingBucket0To30 || "0");
      },
    },
    {
      accessorKey: "agingBucket31To60",
      meta: { align: "right" },
      header: (
        <div>
          Due In
          <br />
          30-60 Days
        </div>
      ),
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucket31To60
          : formatAmount(row?.row.original?.agingBucket31To60 || "0");
      },
    },
    {
      accessorKey: "agingBucket61To90",
      meta: { align: "right" },
      header: (
        <div>
          Due In
          <br />
          60-90 Days
        </div>
      ),
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucket61To90
          : formatAmount(row?.row.original?.agingBucket61To90 || "0");
      },
    },
    {
      accessorKey: "agingBucketOver90",
      meta: { align: "right" },
      header: (
        <div>
          Due <br />
          Over 90 Days
        </div>
      ),
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucketOver90
          : formatAmount(row?.row.original?.agingBucketOver90 || "0");
      },
    },
  ];

  const subColumns = [
    {
      accessorKey: "billedDate",
      header: <div>Doc Date</div>,
    },
    {
      accessorKey: "propertyOwnershipId",
      header: <div>Ownership ID</div>,
    },
    {
      accessorKey: "customerName",
      header: <div>Customer Name</div>,
    },
    {
      accessorKey: "documentType",
      header: <div>Doc Type</div>,
      accessorFn: (row: any) => row?.documentType || "",
    },
    {
      accessorKey: "transactionType",
      header: <div>Transaction Type</div>,
      accessorFn: (row: any) =>
        (transactionTypeValues as any)[row.transactionType] || "",
    },
    // {
    //   accessorKey: "documentNumber",
    //   header: <div>Document No</div>,
    // },
    {
      accessorKey: "charged",
      meta: { align: "right" },
      header: <div>Charged</div>,
      accessorFn: (row: any) => formatAmount(row?.charged || "0"),
    },
    {
      accessorKey: "received",
      meta: { align: "right" },
      header: <div>Received</div>,
      accessorFn: (row: any) =>
        formatAmount(Number(row?.received) === 0 ? "0" : `-${row?.received}`),
    },
    {
      accessorKey: "paymentDate",
      header: <div>Payment Date</div>,
    },
    {
      accessorKey: "lineItemText",
      header: <div>Line Item Text</div>,
    },
    {
      accessorKey: "agingDays",
      header: <div>Aging(Days)</div>,
      accessorFn: (row: any) => {
        return row.documentType?.toLowerCase() == "ob" ? row.agingDays : "";
      },
    },
    {
      accessorKey: "dueDate",
      header: <div>Due Date</div>,
      accessorFn: (row: any) =>
        row.documentType?.toLowerCase() == "ob" ? row.dueDate : "",
    },
  ];

  const onRowExpanded = (row: any) => {
    setSelectedPropertyId(row?.original?.propertyId);
  };

  const onChangeText = (value: string) => {
    setSearchValue(value);
  };

  const onSearch = () => {
    setTableData(
      detailedReport?.data?.data?.filter((val: any) =>
        val.propertyName.includes(searchValue)
      )
    );
  };

  const onClear = () => {
    setSearchValue("");
    setTableData(detailedReport?.data?.data || []);
  };

  return {
    summaryCard,
    columns,
    isLoadingDetailedReport,
    detailedReport,
    subColumns,
    onRowExpanded,
    tableData,
    isLoadingProperties,
    isOwner,
    onChangeText,
    onSearch,
    onClear,
    searchValue,
  };
};

export default usePropertyReport;
